import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

export default function QuadraticCalc() {
  const [a, setA] = useState(1);
  const [b, setB] = useState(-3);
  const [c, setC] = useState(2);
  const [showWork, setShowWork] = useState(false);
  const r = useMemo(() => {
    if (a === 0) return { error: 'a cannot be 0' };
    const disc = b*b - 4*a*c;
    let x1, x2, kind;
    if (disc > 0) {
      x1 = (-b + Math.sqrt(disc))/(2*a); x2 = (-b - Math.sqrt(disc))/(2*a); kind = 'Two real roots';
    } else if (disc === 0) {
      x1 = x2 = -b/(2*a); kind = 'One repeated real root';
    } else {
      const re = -b/(2*a); const im = Math.sqrt(-disc)/(2*a);
      x1 = `${re.toFixed(4)} + ${im.toFixed(4)}i`;
      x2 = `${re.toFixed(4)} − ${im.toFixed(4)}i`;
      kind = 'Complex roots';
    }
    const vx = -b/(2*a); const vy = a*vx*vx + b*vx + c;
    return { disc, x1, x2, kind, vx, vy };
  }, [a, b, c]);

  return (
    <CalcShell look="cnet" title="Quadratic Formula Calculator" subtitle="Solve ax² + bx + c = 0 with roots, discriminant and vertex.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4 text-lg font-semibold text-slate-800">
            <input type="number" value={a} onChange={e=>setA(+e.target.value||0)} className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-center" />x² +
            <input type="number" value={b} onChange={e=>setB(+e.target.value||0)} className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-center" />x +
            <input type="number" value={c} onChange={e=>setC(+e.target.value||0)} className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-center" /> = 0
          </div>
          {r.error ? <div className="text-red-600">{r.error}</div> : (
            <table className="data-table">
              <tbody>
                <tr><td>Discriminant</td><td className="text-right">{r.disc.toFixed(4)}</td></tr>
                <tr><td>Root type</td><td className="text-right">{r.kind}</td></tr>
                <tr><td>x₁</td><td className="text-right font-semibold">{typeof r.x1 === 'number' ? r.x1.toFixed(4) : r.x1}</td></tr>
                <tr><td>x₂</td><td className="text-right font-semibold">{typeof r.x2 === 'number' ? r.x2.toFixed(4) : r.x2}</td></tr>
                <tr><td>Vertex</td><td className="text-right">({r.vx.toFixed(2)}, {r.vy.toFixed(2)})</td></tr>
              </tbody>
            </table>
          )}
        </div>
        <div>
          <ParabolaGraph a={a} b={b} c={c} />
        </div>
      </div>
      {!r.error && <StepsPanel steps={[
        `Discriminant = b² − 4ac = ${b}² − 4·${a}·${c} = <b>${r.disc}</b>`,
        `x = (−b ± √Δ)/(2a)`,
        `Vertex at (−b/2a, f(−b/2a)) = (${r.vx.toFixed(2)}, ${r.vy.toFixed(2)})`
      ]} />}
      {!r.error && (
        <div className="mt-4">
          <button onClick={() => setShowWork(v => !v)} data-testid="quad-show-work-toggle"
            className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
            {showWork ? '− Hide working' : '+ Show working'}
          </button>
          {showWork && (
            <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="quad-work-panel">
              <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
              <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
                <li><b>Equation</b>: <span className="font-mono">{a}x² + {b}x + {c} = 0</span></li>
                <li><b>Quadratic formula</b>: <span className="font-mono">x = (−b ± √(b² − 4ac)) / (2a)</span></li>
                <li><b>Discriminant Δ</b>: <span className="font-mono">Δ = ({b})² − 4·({a})·({c}) = {b*b} − {4*a*c} = <b>{r.disc}</b></span></li>
                {r.disc > 0 && <li>Δ &gt; 0 → <b>two distinct real roots</b>. <span className="font-mono">x = (−{b} ± √{r.disc}) / (2·{a})</span></li>}
                {r.disc === 0 && <li>Δ = 0 → <b>one repeated real root</b>. <span className="font-mono">x = −b / (2a) = {r.x1}</span></li>}
                {r.disc < 0 && <li>Δ &lt; 0 → <b>two complex conjugate roots</b>. <span className="font-mono">x = (−b ± i·√|Δ|) / (2a)</span></li>}
                <li><b>Roots</b>: <b className="text-[#4c7b25]">x₁ = {typeof r.x1 === 'number' ? r.x1.toFixed(4) : r.x1}, x₂ = {typeof r.x2 === 'number' ? r.x2.toFixed(4) : r.x2}</b></li>
                <li><b>Vertex</b>: <span className="font-mono">(−b/2a, f(−b/2a)) = ({r.vx.toFixed(4)}, {r.vy.toFixed(4)})</span> — this is the minimum if a &gt; 0, maximum if a &lt; 0.</li>
              </ol>
            </div>
          )}
        </div>
      )}
    </CalcShell>
  );
}

function ParabolaGraph({ a, b, c }) {
  const pts = [];
  const vx = a !== 0 ? -b/(2*a) : 0;
  const xMin = vx - 5, xMax = vx + 5;
  for (let i = 0; i <= 100; i++) {
    const x = xMin + (xMax - xMin) * i / 100;
    const y = a*x*x + b*x + c;
    pts.push([x, y]);
  }
  const yMin = Math.min(...pts.map(p => p[1]));
  const yMax = Math.max(...pts.map(p => p[1]));
  const w = 320, h = 260, pad = 20;
  const tx = x => pad + (x - xMin)/(xMax - xMin) * (w - 2*pad);
  const ty = y => h - pad - (y - yMin)/(yMax - yMin || 1) * (h - 2*pad);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${tx(p[0])} ${ty(p[1])}`).join(' ');
  return (
    <div className="bg-slate-50 rounded-2xl p-4">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
        <line x1={pad} y1={ty(0)} x2={w-pad} y2={ty(0)} stroke="#cbd5e1" />
        <line x1={tx(0)} y1={pad} x2={tx(0)} y2={h-pad} stroke="#cbd5e1" />
        <path d={path} fill="none" stroke="#7c3aed" strokeWidth="2.5" />
      </svg>
    </div>
  );
}
